import { AuctionService } from '@/services/export/auction/auctionService';
import type { StoreBaseResponse } from '@/types/types';
import type {
  AddUpdateAuctionHouseResponse,
  DataInterface,
  NullableAddUpdateAuctionHouseRequest,
  PaginatedListAuctions,
} from '@/types/auction-types';
import { globalApiService } from '../globalApiSlice';

import type { AuctionFiltersSchemaType } from '@/modules/export/auction/list/components/filter/filtersSchema';
import { createCustomError, createCustomResponse } from '@/helpers/utils';

const service = new AuctionService();

const auctionApi = globalApiService.injectEndpoints({
  endpoints: (build) => ({
    getAuctionList: build.query<
      StoreBaseResponse<PaginatedListAuctions>,
      AuctionFiltersSchemaType['queryParameters']
    >({
      queryFn: async (params) => {
        try {
          const response = await service.getAuctionList(params);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      providesTags: ['auction_addSheet'],
    }),
    getAuctionHouses: build.query<StoreBaseResponse<DataInterface[]>, void>({
      queryFn: async () => {
        try {
          const response = await service.getAuctionHouses();
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    uploadAuctionSheets: build.mutation<StoreBaseResponse<string>, FormData>({
      queryFn: async (data) => {
        try {
          const response = await service.uploadAuctionSheets(data);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['auction_addSheet'],
    }),
    deleteAuctionSheet: build.mutation<
      StoreBaseResponse<null>,
      { auction_sheet_id: number }
    >({
      queryFn: async ({ auction_sheet_id }) => {
        try {
          const response = await service.deleteAuctionSheet(auction_sheet_id);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: ['auction_addSheet'],
    }),
    downloadAuctionSheet: build.mutation<
      StoreBaseResponse<unknown>,
      { auction_sheet_id: number }
    >({
      queryFn: async ({ auction_sheet_id }) => {
        try {
          const response = await service.downloadAuctionSheet(auction_sheet_id);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    previewAuctionSheet: build.mutation<
      StoreBaseResponse<unknown>,
      { auction_sheet_id: number }
    >({
      queryFn: async ({ auction_sheet_id }) => {
        try {
          const response =
            await service.downloadAuctionSheet_preview(auction_sheet_id);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    getAuctionListExport: build.mutation<
      StoreBaseResponse<unknown>,
      AuctionFiltersSchemaType['queryParameters']
    >({
      queryFn: async (params) => {
        try {
          const response = await service.getAuctionListForExport(params);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
    }),
    addUpdateAuctionSheet: build.mutation<
      StoreBaseResponse<AddUpdateAuctionHouseResponse>,
      NullableAddUpdateAuctionHouseRequest
    >({
      queryFn: async (params) => {
        try {
          const response = await service.addUpdateAuctionHouse(params);
          return createCustomResponse(response);
        } catch (error) {
          return createCustomError(error);
        }
      },
      invalidatesTags: (_, error) =>
        error ? [] : ['get_master_auction_house'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAuctionListQuery,
  useGetAuctionHousesQuery,

  useUploadAuctionSheetsMutation,
  useDeleteAuctionSheetMutation,
  useDownloadAuctionSheetMutation,
  useGetAuctionListExportMutation,
  useAddUpdateAuctionSheetMutation,
  usePreviewAuctionSheetMutation,
} = auctionApi;
